<script setup lang="ts">
import { ref, computed, reactive } from 'vue';
import type { ComputedRef } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useForm } from 'vee-validate';
import Message from 'primevue/message';
import DSButton from 'primevue/button';
import InfoPageBusinessInfoFields from './components/InfoPageBusinessInfoFields.vue';
import InfoPageUserInfoFields from './components/InfoPageUserInfoFields.vue';
import UserAddressFields from '@/components/forms/UserAddressFields.vue';
import BusinessAddressFields from '../business/components/BusinessAddressFields.vue';
import { getParsedMessage } from '@/data/error-messages';
import { RouteNames } from '@/data/routes';
import { VerificationService } from '@clearbanc/service-classes/verification-service';
import type { BusinessVerification } from '@clearbanc/service-classes/verification-service';
import { VerificationType } from '@/types/verification-types';
import { ENTITY_TYPE } from '@clearbanc/data-common-types';
import {
  hasBusinessAddressIssue as checkBusinessAddressIssue,
  hasUserAddressIssue as checkUserAddressIssue,
} from '@/utils/verifications';
import type { BusinessDetails } from '@/types/business.types';
import type { RequestStatus } from '@/types/request.types';
import type {
  BusinessVerificationCode,
  UserVerificationCode,
} from '@/data/verifications';

interface VerificationFormFields {
  validateAndGetValues: () => Promise<any>;
}

// -----
// Setup
// -----

const store = useStore();
const router = useRouter();

const formRefBusinessInfo = ref<VerificationFormFields>();
const formRefBusinessAddress = ref<VerificationFormFields>();
const formRefUserInfo = ref<VerificationFormFields>();
const formRefUserAddress = ref<VerificationFormFields>();

// ---------
// Variables
// ---------

const submissionError = ref('');

const requestStatuses = reactive({
  updateBusiness: computed(
    (): RequestStatus => store.getters.requestStatus('UPDATE_BUSINESS'),
  ),
  updateUser: computed(
    (): RequestStatus => store.getters.requestStatus('UPDATE_USER'),
  ),
  postVerification: computed(
    (): RequestStatus => store.getters.requestStatus('POST_VERIFICATION'),
  ),
});

// -------------------
// Computed Properties
// -------------------

const business: ComputedRef<BusinessDetails> = computed(
  () => store.getters.business,
);
const user = computed(() => store.getters.user);
const businessVerification: ComputedRef<BusinessVerification | null> = computed(
  () => store.getters.businessVerification,
);
const businessVerificationCodes: ComputedRef<BusinessVerificationCode[]> =
  computed(() => store.getters.businessVerificationCodes);
const userVerificationCodes: ComputedRef<UserVerificationCode[]> = computed(
  () => store.getters.userVerificationCodes,
);

const hasBusinessAddressIssue: ComputedRef<boolean> = computed(() =>
  checkBusinessAddressIssue(businessVerificationCodes.value),
);

const hasUserAddressIssue: ComputedRef<boolean> = computed(() =>
  checkUserAddressIssue(userVerificationCodes.value),
);

// -------
// Methods
// -------

function processVerificationStatus() {
  if (
    VerificationService.hasPassedBusinessVerification(
      businessVerification.value,
    )
  ) {
    router.push({ name: RouteNames.VERIFICATION.COMPLETE });
    return;
  }

  router.push({
    name: RouteNames.VERIFICATION.DOCUMENTS,
  });
}

const { handleSubmit, isSubmitting } = useForm();

const onSubmit = handleSubmit(async () => {
  submissionError.value = '';

  const businessValues =
    await formRefBusinessInfo.value?.validateAndGetValues();
  const businessAddressValues = hasBusinessAddressIssue.value
    ? await formRefBusinessAddress.value?.validateAndGetValues()
    : {};
  const userValues = await formRefUserInfo.value?.validateAndGetValues();
  const userAddressValues = hasUserAddressIssue.value
    ? await formRefUserAddress.value?.validateAndGetValues()
    : {};

  if (
    !businessValues ||
    (hasBusinessAddressIssue.value && !businessAddressValues) ||
    !userValues ||
    (hasUserAddressIssue.value && !userAddressValues)
  ) {
    submissionError.value = 'Please fix the validation errors.';
    return;
  }

  try {
    // Update business details - PATCH /users/{userId}/business
    if (businessValues || businessAddressValues) {
      const businessPayload = {
        ...businessValues,
        ...businessAddressValues,
      };

      await store.dispatchApiAction('UPDATE_BUSINESS', businessPayload);

      if (requestStatuses.updateBusiness.isError) {
        submissionError.value =
          getParsedMessage(requestStatuses.updateBusiness.error, true) ||
          'Failed to update business details. Please try again.';
        return;
      }
    }

    // Update user details - PATCH /user-management/baus/users/{userId}
    if (userValues || userAddressValues) {
      const userPayload = {
        ...userValues,
        ...userAddressValues,
        id: user.value.id,
      };

      await store.dispatchApiAction('UPDATE_USER', userPayload);

      if (requestStatuses.updateUser.isError) {
        submissionError.value =
          getParsedMessage(requestStatuses.updateUser.error, true) ||
          'Failed to update user details. Please try again.';
        return;
      }
    }

    // Run the verification action after successful updates
    await store.dispatchApiAction('POST_VERIFICATION', {
      type: VerificationType.BusinessInstantId,
      entityId: business.value.id,
      entityType: ENTITY_TYPE.BUSINESS,
      businessId: business.value.id,
    });

    if (requestStatuses.postVerification.isError) {
      submissionError.value =
        getParsedMessage(requestStatuses.postVerification.error, true) ||
        'Verification failed. Please try again.';
      return;
    }

    processVerificationStatus();
  } catch (_error) {
    router.push({ name: RouteNames.VERIFICATION.DOCUMENTS });
  }
});
</script>

<template>
  <form
    class="c-form"
    :style="{ pointerEvents: isSubmitting ? 'none' : 'all' }"
    @submit="onSubmit"
  >
    <div class="c-group">
      <Message severity="warn" :closable="false" :class="$style['message']">
        <h3>Re-enter your information</h3>
        <p>
          We can’t verify some of your information, please double check and
          enter it again.
        </p>
      </Message>

      <Message v-if="submissionError" severity="error" :closable="false">
        <p>{{ submissionError }}</p>
      </Message>
    </div>

    <InfoPageBusinessInfoFields
      :business="business"
      ref="formRefBusinessInfo"
    />
    <BusinessAddressFields
      v-if="hasBusinessAddressIssue"
      :business="business"
      ref="formRefBusinessAddress"
    />
    <InfoPageUserInfoFields :user="user" ref="formRefUserInfo" />
    <UserAddressFields
      v-if="hasUserAddressIssue"
      :user="user"
      ref="formRefUserAddress"
    />

    <div class="c-form__controls" :style="{ flexWrap: 'wrap' }">
      <DSButton
        label="Back"
        class="p-button-outlined"
        @click="
          () => {
            router.push({ name: RouteNames.OWNERS.INDEX });
          }
        "
      />
      <DSButton label="Submit" type="submit" :loading="isSubmitting" />
      <div
        v-if="submissionError"
        class="p-error"
        :class="$style['controls-error']"
      >
        <span>See errors above</span>
      </div>
    </div>
  </form>
</template>

<style module lang="less">
div.message {
  margin: 0;
}

.controls-error {
  text-align: right;
  width: 100%;
  margin-top: 20px;
}
</style>
